const TECHNICAL_FIELD_PATTERN = /\b(primary_symptoms|secondary_symptoms|free_text|severity|diagnosticPayload|rawResult|raw_result|payload|debug|analysis_jobs|schema_version|symptom_report|backendStatus|requestId|job_id|jobId|stack trace|endpoint)\b/i;
const TECHNICAL_REPORT_PATTERN = /RELAT[OÓ]RIO\s+DE\s+INTELIG[ÊE]NCIA\s+MI\s+GERADO/i;
const JSONISH_PATTERN = /(^\s*[\[{])|("[A-Za-z0-9_ -]+"\s*:)|(\b[A-Za-z0-9_]+:\s*[\[{])/;

const TOKEN_LABELS = {
  aim_rises: 'mira subindo no spray',
  aim_drops: 'mira caindo durante o controle',
  aim_shakes: 'mira tremendo para os lados',
  aim_overshoots: 'mira passando do alvo',
  cant_track: 'dificuldade para acompanhar o inimigo',
  recoil_control: 'controle de recoil',
  report_only: 'diagnostico por relato',
  report_plus_video: 'diagnostico com video',
  feedback_refinement: 'refinamento depois do teste',
  pubg_mobile: 'PUBG Mobile',
  free_fire: 'Free Fire',
  codm: 'COD Mobile'
};

export const containsTechnicalLeak = (value) => {
  if (value === null || value === undefined) return false;
  if (typeof value !== 'string') return true;
  return (
    TECHNICAL_FIELD_PATTERN.test(value)
    || TECHNICAL_REPORT_PATTERN.test(value)
    || JSONISH_PATTERN.test(value)
  );
};

export const humanizeTechnicalTokens = (value = '') => {
  let text = String(value || '');
  Object.entries(TOKEN_LABELS).forEach(([token, label]) => {
    text = text.replace(new RegExp(`\\b${token}\\b`, 'gi'), label);
  });
  return text;
};

// Persona: Mi é o nome da assistente, nunca do usuário. Textos gerados por
// LLM às vezes chegam com vocativo errado ("Entendido, Mi!") — removemos o
// vocativo sem tocar em menções legítimas ("com a Mi", "a Mi calcula").
export const stripMiVocative = (value = '') => String(value || '')
  .replace(/,\s*Mi\s*([!.?,;])/g, '$1')
  .replace(/^\s*Mi\s*[,!]\s*/, '');

export const sanitizeMiUserFacingText = (value, fallback = 'Consegui analisar isso. Vou te responder em formato simples, sem dados tecnicos internos.') => {
  if (typeof value !== 'string') return fallback;
  const text = stripMiVocative(humanizeTechnicalTokens(value)).trim();
  if (!text || containsTechnicalLeak(text)) return fallback;
  return text;
};

const gameLabel = (game) => TOKEN_LABELS[game] || game || 'seu jogo';

const legacySkillCopy = {
  sensitivity_diagnostic: ({ context = {} } = {}) => {
    const symptom = context.symptom ? humanizeTechnicalTokens(context.symptom) : null;
    return [
      'Vou tratar isso como diagnostico de sensibilidade.',
      symptom ? `O foco agora e ${symptom}.` : 'Me diga se a mira sobe, cai, treme, passa do alvo ou nao acompanha o inimigo.',
      'Com esse dado eu consigo te orientar com ajuste controlado, teste curto e proximo passo.'
    ].filter(Boolean).join('\n\n');
  },
  calculator_pubg: ({ context = {} } = {}) => [
    'Para calcular PUBG Mobile com seguranca, eu preciso da arma, mira base, valor base e tipo de sensibilidade.',
    context.baseValue ? `Ja vi o valor ${context.baseValue}.` : null,
    'Exemplo: M416 Red Dot 115 ADS Gyro.'
  ].filter(Boolean).join('\n\n'),
  calculator_freefire: () => (
    'Para calcular Free Fire, eu preciso do aparelho, FPS/Hz, quantidade de dedos, estilo de jogo e problema da mira. Me mande isso em uma frase que eu organizo.'
  ),
  mi_analysis_gameplay: ({ context = {} } = {}) => composeVideoAnalysisStart(context),
  mi_analysis_replay: ({ context = {} } = {}) => composeVideoAnalysisStart(context),
  mi_analysis_x1: ({ context = {} } = {}) => composeVideoAnalysisStart({ ...context, analysisGoal: 'x1 e decisao de combate' }),
  mi_analysis_aim: ({ context = {} } = {}) => composeVideoAnalysisStart({ ...context, analysisGoal: 'mira, spray e estabilidade' }),
  mi_analysis_movement: ({ context = {} } = {}) => composeVideoAnalysisStart({ ...context, analysisGoal: 'movimentacao e posicionamento' }),
  training_single_drill: ({ context = {} } = {}) => [
    'Vou transformar isso em treino pratico da Coach.',
    context.drillGoal ? `Foco: ${humanizeTechnicalTokens(context.drillGoal)}.` : 'Me diga a situacao que voce quer treinar: rush, x1, strafe, recoil ou movimentacao.',
    'O treino precisa vir da biblioteca real, entao eu nao vou inventar exercicio fora da Coach.'
  ].filter(Boolean).join('\n\n')
};

export function composeVideoAnalysisStart(context = {}) {
  const goal = sanitizeMiUserFacingText(
    context.analysisGoal || context.validationGoal || context.goal || '',
    ''
  );
  return [
    context.hasVideo
      ? 'Recebi seu video. Vou usar ele como base para analisar sua gameplay.'
      : 'Para analisar gameplay de verdade, me envie um video curto da jogada.',
    goal ? `Foco da analise: ${goal}.` : 'Se quiser, me diga se devo focar em mira, recoil, movimentacao, decisao ou timing.',
    'Quando o processamento terminar, eu vou resumir o erro principal, a causa provavel e o proximo treino em linguagem simples.'
  ].filter(Boolean).join('\n\n');
}

export function composeLegacyCapabilityAnswer({ capabilityId, context = {}, decision = {} } = {}) {
  const composer = legacySkillCopy[capabilityId];
  if (composer) {
    return sanitizeMiUserFacingText(composer({ context, decision }));
  }

  if (decision.reply) {
    return sanitizeMiUserFacingText(decision.reply);
  }

  return 'Consigo continuar por conversa. Me diga o objetivo em uma frase e eu escolho a ferramenta interna certa.';
}

export function composeMiSkillAnswer({
  skillId = null,
  capabilityId = null,
  status = 'success',
  message = null,
  resultText = null,
  slots = {},
  fallback = null
} = {}) {
  const baseFallback = fallback || fallbackForSkill({ skillId, capabilityId, status, slots });
  return sanitizeMiUserFacingText(resultText || message, baseFallback);
}

export function fallbackForSkill({ skillId, capabilityId, status, slots = {} } = {}) {
  if (status === 'needs_slots') {
    return 'Me falta um dado para continuar. Responda so o que eu pedi, sem precisar repetir tudo.';
  }
  if (status === 'invalid') {
    return 'Esse dado nao ficou seguro para executar. Me envie de novo em formato simples.';
  }
  if (status === 'error') {
    return 'Nao consegui concluir agora. Vou manter o contexto e voce pode tentar de novo em instantes.';
  }
  if (skillId === 'pubg_mobile_calculator' || capabilityId === 'calculator_pubg') {
    return [
      'Calculei os valores de PUBG Mobile com a engine oficial.',
      slots.weaponLabel || slots.weapon ? `Arma: ${slots.weaponLabel || slots.weapon}.` : null,
      slots.baseScopeLabel || slots.baseScope ? `Base: ${slots.baseScopeLabel || slots.baseScope} ${slots.baseValue ?? ''}.` : null,
      slots.sensitivityTypeLabel || slots.sensitivityType ? `Tipo: ${slots.sensitivityTypeLabel || slots.sensitivityType}.` : null,
      'Nao misturei Camera, ADS, Gyro e ADS Gyro.'
    ].filter(Boolean).join('\n\n');
  }
  if (skillId === 'sensi_ia' || capabilityId === 'sensitivity_diagnostic') {
    return [
      `Montei um diagnostico de sensibilidade para ${gameLabel(slots.game)}.`,
      'O foco e entender o sintoma, ajustar pouco e validar em treino curto.',
      'Depois do teste, me diga se a mira ainda sobe, cai, treme ou passa do alvo.'
    ].join('\n\n');
  }
  if (skillId === 'coach_training' || capabilityId === 'training_single_drill') {
    return 'Separei um treino da Coach com objetivo, execucao e criterio de acerto. Se quiser, eu continuo ajustando pela sua dificuldade.';
  }
  if (skillId === 'mi_analysis' || String(capabilityId || '').startsWith('mi_analysis')) {
    return composeVideoAnalysisStart({ hasVideo: Boolean(slots.video), analysisGoal: slots.analysisGoal });
  }
  if (skillId === 'freefire_calculator' || capabilityId === 'calculator_freefire') {
    return 'Calculei uma base de Free Fire com os dados informados. Use como ponto inicial e valide em treino antes de ranked.';
  }
  return 'Consegui processar isso. Vou te responder de forma simples e sem dados internos.';
}

export default {
  containsTechnicalLeak,
  sanitizeMiUserFacingText,
  composeMiSkillAnswer,
  composeLegacyCapabilityAnswer,
  composeVideoAnalysisStart
};
